/* eslint-disable no-redeclare */
import { useCallback, useMemo } from 'react';

import {
  LIST_VIEW_PARAMS,
  FilterTypeMapper,
  UseListViewFilters,
  Get,
  LINE_TYPE_VALUES,
} from './useListViewFilters.decl';

import { usePersistSearchParams } from '@components/SearchProvider/usePersistSearchParams';
import { capitalizeFirstLetter } from '@dashboard/library';
import debounce from 'lodash-es/debounce';

export function useListViewFilters(debounceTimeout = 500): UseListViewFilters {
  const { searchParams, setSearchParams } = usePersistSearchParams();

  function parse<T extends LIST_VIEW_PARAMS>(filter: T, value: string): FilterTypeMapper[T] {
    let parsedValue: unknown = value;

    switch (filter) {
      case LIST_VIEW_PARAMS.COUNTRY:
      case LIST_VIEW_PARAMS.TEAMS:
        parsedValue = value.split(',');
        break;

      case LIST_VIEW_PARAMS.SORT_DIRECTION:
        parsedValue = value.toUpperCase();
        break;

      case LIST_VIEW_PARAMS.LINE_STATUS:
        parsedValue = value.toUpperCase().split(',');
        break;

      case LIST_VIEW_PARAMS.ROLES:
      case LIST_VIEW_PARAMS.AVAILABILITY:
        parsedValue = value.split(',').map((role) => capitalizeFirstLetter(role));
        break;

      case LIST_VIEW_PARAMS.LINE_TYPE: {
        parsedValue = value.split(',').map((v) => {
          if (
            [
              LINE_TYPE_VALUES.SMS.toLowerCase(),
              LINE_TYPE_VALUES.MMS.toLowerCase(),
              LINE_TYPE_VALUES.IVR.toLowerCase(),
            ].includes(v)
          ) {
            return v.toUpperCase();
          }
          return capitalizeFirstLetter(v);
        }) as FilterTypeMapper[T];
        break;
      }

      default:
        parsedValue = value;
        break;
    }

    return parsedValue as FilterTypeMapper[T];
  }

  function serialize<T extends LIST_VIEW_PARAMS>(filter: T, value: FilterTypeMapper[T]): string {
    let res: string;
    if (Array.isArray(value)) {
      res = value.join(',');
    } else {
      res = value;
    }

    if ([LIST_VIEW_PARAMS.SEARCH, LIST_VIEW_PARAMS.SORT_FIELD].includes(filter)) {
      return res;
    }

    return res.toLowerCase();
  }

  const set = useCallback(
    <T extends LIST_VIEW_PARAMS>(filter: T, value: FilterTypeMapper[T]) => {
      const valueSerialized = serialize<T>(filter, value);
      setSearchParams({ [filter]: valueSerialized });
    },
    [setSearchParams]
  );

  const setDebounced = useMemo(() => debounce(set, debounceTimeout), [debounceTimeout, set]);

  const setMultiple = useCallback(
    <T extends LIST_VIEW_PARAMS>(filtersValues: Partial<Record<T, FilterTypeMapper[T]>>) => {
      const newSearchParams = Object.entries(filtersValues).reduce((acc, [filter, value]) => {
        const valueSerialized = serialize<T>(filter as T, value as FilterTypeMapper[T]);
        return { ...acc, [filter]: valueSerialized };
      }, {});
      setSearchParams(newSearchParams);
    },
    [setSearchParams]
  );

  const get: Get = useCallback(
    <T extends LIST_VIEW_PARAMS>(filter: T, defaultValue: unknown = null) => {
      const value = searchParams.get(filter);
      if (!value) {
        if (!defaultValue) {
          return null;
        }
        return defaultValue as FilterTypeMapper[T];
      }
      const parsedValue = parse(filter, value);
      return parsedValue;
    },
    [searchParams]
  );

  return { set, setDebounced, setMultiple, get };
}
